import type { CodingGate, World } from "../types";

export const worlds: World[] = [
  {
    id: "attention",
    number: 1,
    name: "注意力森林",
    shortName: "Attention",
    subtitle: "让每个词学会找线索",
    description: "从 Q、K、V 的直觉出发，一路走到张量形状、复杂度与完整 MHA。",
    color: "#43a63c",
    accent: "#d8f56b",
    position: { x: 15, y: 25 },
    quests: [
      {
        id: "decoder-block",
        title: "积木桥的顺序",
        kind: "sequence",
        story: "森林桥由四块积木组成。把它们排成一个 Pre-Norm Decoder Block。",
        intuition: "注意力负责和别的 token 交流，FFN 负责自己想一想；残差像安全绳，把旧信息留住。",
        question: "按前向计算顺序点亮四块积木。",
        pieces: ["FFN(Norm(X′))", "X + Attention(Norm(X))", "得到 X′", "X′ + FFN(...)"],
        order: ["X + Attention(Norm(X))", "得到 X′", "FFN(Norm(X′))", "X′ + FFN(...)"],
        explanation: "Pre-Norm 先归一化再进入子层，每个子层外面都有残差连接。",
        interviewTip: "训练能并行算整段 token，但自回归生成仍要逐 token 进行。",
        sourcePages: "手册第 3 页",
      },
      {
        id: "qkv-roles",
        title: "三只线索精灵",
        kind: "quiz",
        story: "Query、Key、Value 精灵走散了。机器人问：谁真正携带被读取的内容？",
        intuition: "Q 是“我想找什么”，K 是“我能被怎样找到”，V 是“找到后拿走什么”。",
        question: "匹配成功后，真正被加权汇总的是谁？",
        options: ["Query", "Key", "Value", "Causal Mask"],
        correct: 2,
        explanation: "Q 与 K 决定匹配分数，Softmax 得到权重，权重最后乘到 V 上。",
        interviewTip: "Key 决定匹配，Value 决定读取内容；两者处在不同投影空间。",
        sourcePages: "手册第 3-4 页",
      },
      {
        id: "scale-sqrt",
        title: "驯服大嗓门",
        kind: "quiz",
        story: "head 维度越大，点积说话越大声，Softmax 被吓得只听一个人。",
        intuition: "点积方差约是 dₖ，标准差约是 √dₖ，所以要除掉标准差。",
        question: "为什么缩放因子是 √dₖ，而不是 dₖ？",
        options: ["为了让矩阵变方", "因为校正的是标准差", "为了减少 head 数", "因为 Softmax 只能接收整数"],
        correct: 1,
        explanation: "若各维独立且方差近似 1，qᵀk 的方差随 dₖ 增长，标准差随 √dₖ 增长。",
        interviewTip: "不缩放会使 logits 绝对值过大、Softmax 饱和、大多数梯度变小。",
        sourcePages: "手册第 4-5 页",
      },
      {
        id: "causal-mask",
        title: "未来偷看怪",
        kind: "mask",
        story: "请封住所有“从现在偷看未来”的窗格。行是 Query 位置，列是 Key 位置。",
        intuition: "第 i 个 token 只能看 j ≤ i；右上三角必须在 Softmax 前变成 −∞。",
        question: "点亮 4×4 棋盘中所有需要被 Mask 的格子。",
        explanation: "未来位置 j > i 位于严格上三角。先 Mask 再 Softmax，exp(−∞)=0。",
        interviewTip: "若 Softmax 后再乘 0，剩余概率和不再为 1，还要重新归一化。",
        sourcePages: "手册第 5 页",
      },
      {
        id: "head-shapes",
        title: "多头变形术",
        kind: "sequence",
        story: "把 [B,L,D] 变成多个小侦探的视角，再安全合回去。",
        intuition: "先 reshape 把 D 拆成 H×Dₕ，再 transpose 把 H 搬到 L 前面。",
        question: "按正确顺序拼出多头切分与合并。",
        pieces: ["[B,H,L,Dₕ]", "[B,L,H,Dₕ]", "[B,L,D]", "transpose 回 [B,L,H,Dₕ]"],
        order: ["[B,L,D]", "[B,L,H,Dₕ]", "[B,H,L,Dₕ]", "transpose 回 [B,L,H,Dₕ]"],
        explanation: "切分时 reshape 后 transpose；合并时先 transpose 回来，再 reshape 为 D。",
        interviewTip: "最常见错误是 transpose 维度写错，务必逐步写出形状。",
        sourcePages: "手册第 5、8-11 页",
      },
      {
        id: "mha-complexity",
        title: "复杂度跷跷板",
        kind: "quiz",
        story: "投影石和注意力石要上跷跷板。哪一个在超长序列里越来越重？",
        intuition: "完整主要计算是 BLd² + BL²d；比较 d 与 L 就知道谁占主导。",
        question: "当 L ≫ d 时，哪个部分更可能成为瓶颈？",
        options: ["O(BLd²) 投影", "O(BL²d) Attention", "O(BL) 输入检查", "O(Bd) 输出偏置"],
        correct: 1,
        explanation: "约去公共项 BLd 后，比较的是 d 与 L；L 更大时二次 Attention 项主导。",
        interviewTip: "标准 Attention map 形状 [B,H,L,L]，激活显存约 O(BHL²)。",
        sourcePages: "手册第 6-7 页",
      },
      {
        id: "mha-workshop",
        title: "NumPy 手写工坊",
        kind: "quiz",
        story: "终点不是背出整段代码，而是把 14 个关键逻辑块逐一装进机器。",
        intuition: "每一关只解决一个问题：形状、缩放、Mask、Softmax 或聚合。",
        question: "手写 MHA 时，最可靠的第一检查是什么？",
        options: ["先优化速度", "先确认输入是 [B,L,D]", "先写输出投影", "先删除 Mask"],
        correct: 1,
        explanation: "形状是整段实现的地图。确认输入和 D 能整除 head 数，再继续切头。",
        interviewTip: "通过后会开启 14 关的独立 Coding 工坊。",
        sourcePages: "手册第 8-11 页",
      },
    ],
  },
  {
    id: "inference",
    number: 2,
    name: "缓存工坊",
    shortName: "Inference",
    subtitle: "少搬东西，机器就跑得快",
    description: "分清 MHA/MQA/GQA/MLA、FlashAttention、KV Cache 与 Prompt Cache 的分工。",
    color: "#f39a22",
    accent: "#ffe36e",
    position: { x: 46, y: 17 },
    quests: [
      {
        id: "kv-family",
        title: "参考书分配员",
        kind: "quiz",
        story: "全班要共享参考书。你可以每人一本、全班一本、小组一本，或压成索引卡。",
        intuition: "MHA 独立 KV；MQA 全共享；GQA 分组共享；MLA 缓存低维 latent。",
        question: "“每 4 个 Query heads 共用一套 KV”是哪种方法？",
        options: ["MHA", "MQA", "GQA", "MLA"],
        correct: 2,
        explanation: "GQA 在多个 KV group 之间折中；KV heads 等于 Query heads 时退化为 MHA，等于 1 时是 MQA。",
        interviewTip: "这些方法主要降低 decode 的 KV Cache 容量与读取带宽，并未消灭训练时 L² Attention。",
        sourcePages: "手册第 12-14 页",
      },
      {
        id: "mla",
        title: "高密度索引卡",
        kind: "quiz",
        story: "工程师不再缓存整套参考书，而是存一张能展开信息的低维索引卡。",
        intuition: "MLA 不是简单减少 KV head，而是压缩 KV 信息并解耦位置分量。",
        question: "MLA 与 GQA 的本质区别是什么？",
        options: ["MLA 取消 Query", "MLA 缓存低维 KV latent", "GQA 不使用 Softmax", "MLA 把复杂度变成 O(L)"],
        correct: 1,
        explanation: "MLA 通过下投影得到低维 latent，推理时主要缓存 latent，并用上投影或矩阵吸收参与计算。",
        interviewTip: "MLA 架构与 kernel 更复杂，RoPE 位置 Key 分量通常单独处理。",
        sourcePages: "手册第 13-14 页",
      },
      {
        id: "kv-math",
        title: "显存称重台",
        kind: "quiz",
        story: "32 层、32 个 KV heads、head_dim 128、长度 32768、FP16、batch 1。",
        intuition: "元素数 = 2(K/V)×B×L×layers×KV heads×head_dim，再乘 bytes。",
        question: "这套 MHA KV Cache 大约多大？",
        options: ["0.5 GiB", "4 GiB", "16 GiB", "64 GiB"],
        correct: 2,
        explanation: "2×1×32768×32×32×128×2 bytes ≈ 16 GiB。8 个 KV heads 的 GQA 约 4 GiB。",
        interviewTip: "减少 KV Cache 还能容纳更大 batch 和更多并发，吞吐收益常比单请求延迟更关键。",
        sourcePages: "手册第 14-15 页",
      },
      {
        id: "flash-attention",
        title: "搬运工的秘密",
        kind: "sequence",
        story: "FlashAttention 不少算，而是少把巨大的 L×L 矩阵搬进搬出 HBM。",
        intuition: "把 Q/K/V 分块放进片上存储，用 online softmax 维护最大值、指数和与加权输出。",
        question: "按 FlashAttention 的核心流程排序。",
        pieces: ["更新 online softmax 统计量", "分块读取 K/V", "Query tile 放入片上存储", "直接累积输出"],
        order: ["Query tile 放入片上存储", "分块读取 K/V", "更新 online softmax 统计量", "直接累积输出"],
        explanation: "完整 score/probability 不落 HBM，激活显存关于序列长度近似从二次降到线性。",
        interviewTip: "精确 Attention FLOPs 仍约 O(L²d)；它是 IO-aware，不是稀疏近似。",
        sourcePages: "手册第 20-21 页",
      },
      {
        id: "prefill-decode",
        title: "整车与单件快递",
        kind: "quiz",
        story: "Prompt 进站时像整车卸货；生成每个 token 时像一次送一个小包，却要翻遍历史仓库。",
        intuition: "Prefill 大矩阵、高并行、偏 compute-bound；Decode 瘦矩阵、读全部 KV、偏 bandwidth-bound。",
        question: "哪个描述属于 Decode？",
        options: ["一次处理完整 Prompt", "主要生成整段 KV", "Query 长度通常为 1", "总是更偏 compute-bound"],
        correct: 2,
        explanation: "Decode 每步 Query 长度通常为 1，但仍需读取全部历史 KV。",
        interviewTip: "优化 prefill 的方法不一定同样优化 decode。",
        sourcePages: "手册第 22 页",
      },
      {
        id: "cache-scope",
        title: "缓存通行证",
        kind: "quiz",
        story: "一张票只能在本次旅程使用，另一张票能让后续旅客复用完全相同的公共前缀。",
        intuition: "KV Cache 在同一请求内复用历史；Prompt Cache 在请求之间复用公共前缀 prefill。",
        question: "跨多个请求复用相同 system prompt 的技术是？",
        options: ["KV Cache", "Prompt / Prefix Cache", "Causal Mask", "RoPE"],
        correct: 1,
        explanation: "命中通常要求模型、Tokenizer、token 前缀、位置配置都兼容且精确一致。",
        interviewTip: "KV Cache 没消除当前 Query 对所有历史 K/V 的读取，所以长上下文 decode 仍越来越慢。",
        sourcePages: "手册第 22-24 页",
      },
    ],
  },
  {
    id: "rope",
    number: 3,
    name: "旋转星港",
    shortName: "RoPE",
    subtitle: "让距离藏进相对角度",
    description: "从二维旋转直觉一路理解 PI、NTK-aware、YaRN 与 LongRoPE。",
    color: "#1677df",
    accent: "#70ddff",
    position: { x: 79, y: 22 },
    quests: [
      {
        id: "rope-relative",
        title: "相对角度罗盘",
        kind: "sequence",
        story: "两个 token 分别在 m 和 n 位置旋转。把点积推到只剩相对距离。",
        intuition: "R(mθ)ᵀR(nθ)=R((n−m)θ)，所以绝对位置在点积里相消。",
        question: "按推导顺序拼起罗盘。",
        pieces: ["qᵀR((n−m)θ)k", "qₘ=R(mθ)q", "qₘᵀkₙ", "kₙ=R(nθ)k"],
        order: ["qₘ=R(mθ)q", "kₙ=R(nθ)k", "qₘᵀkₙ", "qᵀR((n−m)θ)k"],
        explanation: "旋转矩阵的组合使 Attention 点积自然依赖 n−m。",
        interviewTip: "RoPE 是乘到 Q/K 二维对子上的旋转，不是把位置向量直接加到 embedding。",
        sourcePages: "手册第 16-17 页",
      },
      {
        id: "rope-extrapolation",
        title: "十万号站台",
        kind: "quiz",
        story: "模型只在 4096 以内训练，现在列车突然开到位置 100000。",
        intuition: "公式有相对结构，不等于模型见过训练外的旋转相位组合。",
        question: "RoPE 不能天然无限外推的核心原因是？",
        options: ["位置必须是负数", "训练外相位分布偏移", "Softmax 没有指数", "Value 不能旋转"],
        correct: 1,
        explanation: "高频维度旋转多圈，维度间相位组合与 logits 统计都可能偏离训练分布。",
        interviewTip: "不要把数学形式的相对位置结构，夸大成模型的无限上下文能力。",
        sourcePages: "手册第 17 页",
      },
      {
        id: "position-interpolation",
        title: "缩小的星图",
        kind: "quiz",
        story: "把 16384 的星图压回训练时的 4096 范围，所有距离都缩小 4 倍。",
        intuition: "Position Interpolation 用 m′=m/s 避免超出原相位范围，但牺牲局部分辨率。",
        question: "原位置 12000 在 s=4 时映射到哪里？",
        options: ["3000", "4096", "12004", "48000"],
        correct: 0,
        explanation: "12000÷4=3000，回到训练窗口内。",
        interviewTip: "它的风险是所有频率一刀切，局部距离也被压缩。",
        sourcePages: "手册第 18 页",
      },
      {
        id: "rope-methods",
        title: "四把伸缩尺",
        kind: "sequence",
        story: "从最简单到更精细，把四种长度扩展思路排好。",
        intuition: "PI 全频率统一缩放；NTK-aware 非均匀调频；YaRN 再顾及 Attention 尺度；LongRoPE 搜索维度相关缩放并渐进扩展。",
        question: "按方法能力逐步增强排序。",
        pieces: ["YaRN", "Position Interpolation", "LongRoPE", "NTK-aware"],
        order: ["Position Interpolation", "NTK-aware", "YaRN", "LongRoPE"],
        explanation: "这不是绝对性能排名，而是一条从统一缩放到维度相关搜索与渐进扩展的设计链。",
        interviewTip: "评估还要看位置检索、长文任务与短上下文回退，不能只看 perplexity。",
        sourcePages: "手册第 18-19 页",
      },
      {
        id: "dynamic-ntk",
        title: "会变长的尺子",
        kind: "quiz",
        story: "短文本保持原刻度，超过训练窗口后才逐渐拉伸。",
        intuition: "Dynamic NTK 根据实际输入长度决定缩放强度。",
        question: "它在工程上最需要小心什么？",
        options: ["同一位置在不同最大长度下编码可能不同", "不能处理短文本", "必须删除 KV Cache", "只能使用一个频率"],
        correct: 0,
        explanation: "动态缩放可能破坏位置一致性，因此 Prefix Cache 与复用规则要谨慎。",
        interviewTip: "短文本退化较小是优势，缓存一致性复杂是代价。",
        sourcePages: "手册第 19 页",
      },
    ],
  },
  {
    id: "ppo",
    number: 4,
    name: "训练学院",
    shortName: "SFT · PPO",
    subtitle: "先学示范，再在反馈中成长",
    description: "从语言模型策略、SFT 边界到 PPO 四角色、Advantage、GAE、Clip 与 KL。",
    color: "#2a8bca",
    accent: "#b7f0ff",
    position: { x: 27, y: 60 },
    quests: [
      {
        id: "sft-strength",
        title: "模仿课",
        kind: "quiz",
        story: "老师给出高质量轨迹，学生逐 token 模仿。",
        intuition: "SFT 是行为克隆，擅长格式、知识、语言质量和工具接口。",
        question: "下面哪项不是 SFT 的强项？",
        options: ["学习输出格式", "建立指令跟随", "主动发现数据中没有的新策略", "提供 RL 初始策略"],
        correct: 2,
        explanation: "SFT 主要模仿数据里的道路，不会主动探索未展示的策略。",
        interviewTip: "SFT 只学正例 token likelihood，不直接表达 A 比 B 好。",
        sourcePages: "手册第 25-27 页",
      },
      {
        id: "sft-or-rl",
        title: "该不该去探险",
        kind: "quiz",
        story: "有些任务示范充分且答案确定，有些任务需要多步探索和终局反馈。",
        intuition: "RL 不天然更高级；关键看示范覆盖、verifier 可靠性与探索需求。",
        question: "哪个场景更值得加入 RL？",
        options: ["格式固定且标准答案丰富", "Reward 很不可靠", "多步工具交互且有可靠 verifier", "计算资源极少"],
        correct: 2,
        explanation: "有可靠环境反馈、多种策略和探索空间时，RL 能根据当前行为重新分配概率。",
        interviewTip: "错误 Reward 下，RL 可能比 SFT 更糟。",
        sourcePages: "手册第 28-29 页",
      },
      {
        id: "ppo-cast",
        title: "四位老师",
        kind: "sequence",
        story: "给 PPO 的四个角色安排工作。",
        intuition: "Actor 答题，Critic 估分，Reward Model 终审，Reference Model 守住语言底线。",
        question: "按一次训练循环中最自然的出现顺序排列。",
        pieces: ["Critic 预测 Value", "Actor 采样回答", "Reference 提供 KL", "Reward Model 打分"],
        order: ["Actor 采样回答", "Reward Model 打分", "Reference 提供 KL", "Critic 预测 Value"],
        explanation: "之后计算 Advantage，用 clipped objective 更新 Actor，并用 return 拟合 Critic。",
        interviewTip: "工程上 Actor/Critic 可共享 backbone，但概念职责仍不同。",
        sourcePages: "手册第 30-31 页",
      },
      {
        id: "advantage",
        title: "比平时好多少",
        kind: "quiz",
        story: "同样得 8 分，对平时只得 3 分的学生和平均 9 分的学生意义不同。",
        intuition: "A(s,a)=Q(s,a)−V(s)，衡量动作相对该状态平均水平好多少。",
        question: "当 Advantage < 0 时，策略应该怎样对待这个动作？",
        options: ["提高概率", "降低概率", "删除状态", "把 Reward 设为 1"],
        correct: 1,
        explanation: "负 Advantage 表示该动作劣于状态基线，应降低其概率。",
        interviewTip: "Value baseline 能降低策略梯度方差。",
        sourcePages: "手册第 32 页",
      },
      {
        id: "gae",
        title: "远近望远镜",
        kind: "quiz",
        story: "λ 是望远镜旋钮：小 λ 多看近期 TD，大 λ 更接近整条 Monte Carlo 回报。",
        intuition: "GAE 在偏差与方差之间折中。",
        question: "λ 变大通常意味着什么？",
        options: ["偏差更大、方差更小", "更接近 Monte Carlo，方差更大", "完全不看 Reward", "Critic 被删除"],
        correct: 1,
        explanation: "λ 较大使用更长时域的 TD error，偏差较小但方差通常较大。",
        interviewTip: "一步 TD error 是 δₜ=rₜ+γV(sₜ₊₁)−V(sₜ)。",
        sourcePages: "手册第 33 页",
      },
      {
        id: "ppo-clip",
        title: "更新护栏",
        kind: "quiz",
        story: "好动作可以加速，但不能一脚油门冲出道路；坏动作也不能一次打入冷宫。",
        intuition: "ratio 比较当前策略与 rollout old policy；Clip 限制同一批数据上的更新幅度。",
        question: "A>0、r=1.5、ε=0.2 时，clipped 项的上限是多少？",
        options: ["0.8", "1.0", "1.2", "1.5"],
        correct: 2,
        explanation: "正 Advantage 时上界为 1+ε=1.2，继续增加概率不再获得额外目标收益。",
        interviewTip: "Clip 参照 old policy；KL 参照 frozen reference，时间尺度不同。",
        sourcePages: "手册第 34-38 页",
      },
      {
        id: "kl",
        title: "参考航线",
        kind: "quiz",
        story: "飞船要探索新航线，又不能远离语言质量与安全底线。",
        intuition: "KL 太大几乎学不到新行为，太小则容易漂移与 Reward hacking。",
        question: "PPO 中 Reference KL 主要防止什么？",
        options: ["任何参数更新", "策略总体漂移过远", "读取 Prompt", "计算 Advantage"],
        correct: 1,
        explanation: "Reference KL 维持语言先验并抑制重复、长度膨胀与钻奖励漏洞。",
        interviewTip: "Clip 与 KL 可同时使用，因为约束对象并不相同。",
        sourcePages: "手册第 38 页",
      },
    ],
  },
  {
    id: "preference",
    number: 5,
    name: "偏好峡谷",
    shortName: "DPO · GRPO",
    subtitle: "比较答案，奖励会推理的路线",
    description: "看懂 DPO 隐式奖励、GRPO 组相对优势，以及 DAPO 如何修复长 CoT 训练。",
    color: "#a25bd7",
    accent: "#ff9ea8",
    position: { x: 50, y: 62 },
    quests: [
      {
        id: "dpo-pair",
        title: "两份答案",
        kind: "quiz",
        story: "面前有 preferred 与 rejected 两份回答，DPO 不搭建在线考场，直接学习偏好差。",
        intuition: "DPO 增大 preferred 相对 reference 的 log-ratio 优势，并降低 rejected 的优势。",
        question: "标准 DPO 的训练数据是什么？",
        options: ["单个正确 token", "(prompt, preferred, rejected)", "环境状态转移", "只有 Reward 标量"],
        correct: 1,
        explanation: "同一 Prompt 的两份回答构成偏好对。",
        interviewTip: "标准 DPO 是离线分类式优化，没有 on-policy rollout 或 Critic。",
        sourcePages: "手册第 39-42 页",
      },
      {
        id: "dpo-implicit",
        title: "藏起来的裁判",
        kind: "sequence",
        story: "从 KL 正则化目标找到隐藏在策略与 Reference 比值里的 Reward。",
        intuition: "最优策略是 π*∝πref·exp(r/β)，所以 Reward 可由 log 概率比表示。",
        question: "按推导链条排序。",
        pieces: ["同一 Prompt 的 log Z 相消", "写出 Gibbs 最优策略", "得到 DPO 偏好损失", "代入 Bradley-Terry"],
        order: ["写出 Gibbs 最优策略", "代入 Bradley-Terry", "同一 Prompt 的 log Z 相消", "得到 DPO 偏好损失"],
        explanation: "因此无需显式训练 Reward Model，就能直接优化 Policy/Reference 的相对偏好。",
        interviewTip: "隐式 Reward 可写成 β log(πθ/πref)+C(x)。",
        sourcePages: "手册第 40-41 页",
      },
      {
        id: "ppo-vs-dpo",
        title: "考场还是题库",
        kind: "quiz",
        story: "PPO 让模型进入环境考试，DPO 用提前准备好的两份答案练习偏好。",
        intuition: "PPO 支持在线探索但工程重；DPO 简单稳定但受离线覆盖限制。",
        question: "哪项只属于 PPO？",
        options: ["需要 Reference", "可在线采样与环境交互", "能使用偏好", "有 Policy 模型"],
        correct: 1,
        explanation: "标准 DPO 不做当前策略 rollout，不直接探索环境。",
        interviewTip: "DPO 理论来自 RLHF，但训练过程不是典型 on-policy RL。",
        sourcePages: "手册第 42 页",
      },
      {
        id: "grpo",
        title: "组内擂台",
        kind: "quiz",
        story: "同一道题采样 G 个回答，用组内均值和标准差建立相对 baseline。",
        intuition: "其他回答替代 Critic：Âᵢ=(Rᵢ−μR)/(σR+ε)。",
        question: "GRPO 为什么不需要 Critic？",
        options: ["不需要 Reward", "用同 Prompt 的组内 Reward 作 baseline", "不更新 Policy", "每组只采一个回答"],
        correct: 1,
        explanation: "组内相对比较提供 Advantage 估计，省去 Value Model。",
        interviewTip: "代价是每个 Prompt 要多采样，response-level credit assignment 也更粗。",
        sourcePages: "手册第 43-45 页",
      },
      {
        id: "grpo-flat",
        title: "全员同分陷阱",
        kind: "quiz",
        story: "八份回答不是全对就是全错，分数完全相同。",
        intuition: "σR=0 时归一化 Advantage 接近 0，训练预算没有产生有效梯度。",
        question: "最直接的改进是什么？",
        options: ["删掉 Softmax", "Dynamic Sampling 重采样同分组", "把 group size 设为 1", "固定所有 Reward"],
        correct: 1,
        explanation: "保留同时含成功与失败回答的 group，才能产生比较信号。",
        interviewTip: "过滤会增加 rollout，也可能降低极难题的占比。",
        sourcePages: "手册第 45-46、51 页",
      },
      {
        id: "r1-recipe",
        title: "推理成长食谱",
        kind: "sequence",
        story: "纯 RL 会冒出推理和反思，也可能语言混乱；完整 R1 把 SFT 与 RL 交替使用。",
        intuition: "SFT 给行为与语言先验，RL 探索，再把好轨迹蒸馏回 SFT。",
        question: "拼出简化版训练食谱。",
        pieces: ["通用第二阶段 RL", "cold-start SFT", "reasoning RL", "拒绝采样后再 SFT"],
        order: ["cold-start SFT", "reasoning RL", "拒绝采样后再 SFT", "通用第二阶段 RL"],
        explanation: "实践不是 SFT 与 RL 二选一，而是 SFT → RL → SFT → RL。",
        interviewTip: "R1-Zero 展示纯 RL 激发推理；完整 R1 解决可读性和通用表达。",
        sourcePages: "手册第 47-48 页",
      },
      {
        id: "dapo-four",
        title: "DAPO 四件套",
        kind: "sequence",
        story: "长 CoT 训练遇到熵塌缩、无效 group、长度权重偏差和悬崖式超长惩罚。",
        intuition: "四个修补工具分别照顾探索、采样、token 权重和奖励连续性。",
        question: "按手册顺序收集四件套。",
        pieces: ["Overlong Reward Shaping", "Dynamic Sampling", "Clip-Higher", "Token-Level Policy Gradient"],
        order: ["Clip-Higher", "Dynamic Sampling", "Token-Level Policy Gradient", "Overlong Reward Shaping"],
        explanation: "Clip-Higher 放宽好但罕见 token 上升；Dynamic Sampling 筛有效组；token-level 每 token 等权；Overlong 平滑惩罚。",
        interviewTip: "DAPO 原设置移除 KL，不代表所有推理 RL 都应移除 KL。",
        sourcePages: "手册第 49-54 页",
      },
    ],
  },
  {
    id: "agent",
    number: 6,
    name: "Agent 实验室",
    shortName: "RLVR",
    subtitle: "让真实结果说了算",
    description: "把程序、测试和环境变成 verifier，并亲手设计一套不容易被钻漏洞的 Reward。",
    color: "#45bfa6",
    accent: "#c7ffdd",
    position: { x: 81, y: 67 },
    quests: [
      {
        id: "rlvr",
        title: "可以验算的奖励",
        kind: "quiz",
        story: "答案不是“看起来不错”就得分，而要通过测试、编译器、SQL 结果或最终页面状态。",
        intuition: "RLVR 的 Reward 主要来自程序或环境 verifier。",
        question: "下面哪个最符合 RLVR verifier？",
        options: ["模型自称成功", "代码单元测试", "随机点赞", "只看回答长度"],
        correct: 1,
        explanation: "单元测试能检查代码是否真实满足预期，而不是表面上像正确。",
        interviewTip: "Verifier 仍会被钻空子：它只验证被编程检查的部分。",
        sourcePages: "手册第 55 页",
      },
      {
        id: "agent-loop",
        title: "修 Bug 轨迹",
        kind: "sequence",
        story: "Agent 接到 GitHub Issue，要在真实仓库中行动并接受测试验证。",
        intuition: "状态包含目标、历史、观察和工具结果；动作可以是文本、工具调用或 GUI 操作。",
        question: "拼出一条合理的修复轨迹。",
        pieces: ["修改实现", "阅读 Issue 与搜索代码", "运行测试", "根据报错继续修改"],
        order: ["阅读 Issue 与搜索代码", "修改实现", "运行测试", "根据报错继续修改"],
        explanation: "完整轨迹最终由环境状态与目标的 verifier 判断。",
        interviewTip: "综合 Reward 可结合 tests、compile、hidden tests，并扣除非法调用与成本。",
        sourcePages: "手册第 56-57 页",
      },
      {
        id: "sparse-reward",
        title: "零分荒漠",
        kind: "quiz",
        story: "Agent 连续 100 步后失败；同组所有轨迹都是 0 分。",
        intuition: "终局 Reward 太稀疏时无法知道哪一步出错，组相对算法也没有比较信号。",
        question: "哪个办法能直接提供更密集的学习信号？",
        options: ["把轨迹拉得更长", "Process verifier / subgoal reward", "隐藏所有工具结果", "让所有组只采一条"],
        correct: 1,
        explanation: "过程奖励可以标记子目标、合法工具调用或测试数量的增量，帮助 credit assignment。",
        interviewTip: "还可用 curriculum、expert demonstrations、guidance 与失败早停。",
        sourcePages: "手册第 58-60 页",
      },
      {
        id: "verifier-hacking",
        title: "空文件骗局",
        kind: "quiz",
        story: "任务要写入正确内容，但 verifier 只检查文件存在；Agent 创建空文件就拿到奖励。",
        intuition: "检查表面状态会鼓励“高效走错路”。",
        question: "正确修复 verifier 的方式是？",
        options: ["只检查文件名更长", "检查文件内容与真实语义目标", "奖励更多工具调用", "忽略最终状态"],
        correct: 1,
        explanation: "Verifier 应验证真实目标，而不只是可轻易触发的代理指标。",
        interviewTip: "隐藏测试与约束检查可以降低针对可见规则的投机。",
        sourcePages: "手册第 59 页",
      },
      {
        id: "outcome-process",
        title: "终点旗与路标",
        kind: "quiz",
        story: "终点旗判断是否真的成功，沿路路标告诉 Agent 哪一步正在接近目标。",
        intuition: "Outcome 决定方向；Process 帮助归因，但错误过程奖会限制探索。",
        question: "只检查最终 task success 的 Reward 是？",
        options: ["Outcome Reward", "Process Reward", "Entropy Bonus", "Reference KL"],
        correct: 0,
        explanation: "Outcome 客观且不限定路线，但稀疏；Process 密集但设计复杂。",
        interviewTip: "组合使用时要让 Outcome 保持最高语义权威。",
        sourcePages: "手册第 60-61 页",
      },
      {
        id: "reward-builder",
        title: "奖励配方台",
        kind: "sequence",
        story: "为 Web Agent 配一份既追求成功、又限制成本和危险捷径的 Reward。",
        intuition: "R=αRsuccess+βRprogress+γRvalid−λRcost−ηRsafety。",
        question: "从最重要的方向信号到辅助约束排序。",
        pieces: ["Cost：token/steps/latency", "Success：隐藏 verifier 通过", "Valid：合法工具调用", "Progress：子目标完成"],
        order: ["Success：隐藏 verifier 通过", "Progress：子目标完成", "Valid：合法工具调用", "Cost：token/steps/latency"],
        explanation: "成功决定方向，过程帮助归因，有效性减少无用调用，成本防止绕地球三圈。安全项还应单独堵危险捷径。",
        interviewTip: "绝不能因为 Agent 最后声称“成功”就给分，必须查真实环境状态。",
        sourcePages: "手册第 62、66 页",
      },
      {
        id: "boss-interview",
        title: "面试 Boss 战",
        kind: "quiz",
        story: "最后一问：为什么有 KV Cache 后，长上下文生成仍越来越慢？",
        intuition: "缓存省掉历史 K/V 的重复投影，却没省掉当前 Query 对全部历史的读取。",
        question: "最完整的答案是哪一个？",
        options: [
          "因为 KV Cache 会随机失效",
          "新 Query 仍与所有历史 Keys 做点积并读取 Values，单步开销随 t 增长",
          "因为 Softmax 只能在 CPU 上运行",
          "因为 Prompt Cache 删除了历史 token"
        ],
        correct: 1,
        explanation: "第 t 步的 Attention 与 KV 读取量约 O(td)，所以 decode latency 随上下文长度增长。",
        interviewTip: "回答任何方法都用：瓶颈 → 修改 → 为什么有效 → 代价与边界。",
        sourcePages: "手册第 63-69 页",
      },
    ],
  },
];

export const codingGates: CodingGate[] = [
  {
    id: "numpy-import",
    title: "叫醒 NumPy",
    mission: "先请来我们的矩阵工具箱。",
    intuition: "NumPy 就像装满矩阵积木的工具箱，习惯简称 np。",
    shape: "工具准备",
    before: "",
    after: "\n\ndef softmax(x, axis=-1):\n    ...",
    options: ["import numpy as np", "from numpy import torch", "import attention as np"],
    answer: 0,
    hint: "后面的代码一直用 np.sqrt、np.exp。",
    success: "工具箱已经打开！",
    sourcePages: "手册第 8 页",
  },
  {
    id: "softmax-max",
    title: "给 Softmax 降温",
    mission: "先减去每一行最大值，防止 exp 溢出。",
    intuition: "所有 logits 同减一个常数，Softmax 概率不变，但数字更安全。",
    shape: "[..., L] → [..., L]",
    before: "def softmax(x, axis=-1):\n",
    after: "\n    exp_x = np.exp(x)\n    return exp_x / np.sum(exp_x, axis=axis, keepdims=True)",
    options: [
      "    x = x - np.max(x, axis=axis, keepdims=True)",
      "    x = x / np.sum(x)",
      "    x = np.max(x)"
    ],
    answer: 0,
    hint: "要保持被减数的维度，才能正确广播。",
    success: "Softmax 不再被超大数字烫伤。",
    sourcePages: "手册第 8 页",
  },
  {
    id: "softmax-exp",
    title: "变成正权重",
    mission: "把稳定后的 logits 送进指数函数。",
    intuition: "exp 会把任意实数变成正数，为概率归一化做准备。",
    shape: "logits → positive weights",
    before: "x = x - np.max(x, axis=axis, keepdims=True)\n",
    after: "\nreturn exp_x / np.sum(exp_x, axis=axis, keepdims=True)",
    options: ["exp_x = np.exp(x)", "exp_x = np.log(x)", "exp_x = x.astype(bool)"],
    answer: 0,
    hint: "它和自然对数互为反函数。",
    success: "每个权重都变成正数了。",
    sourcePages: "手册第 8 页",
  },
  {
    id: "head-divisible",
    title: "公平分头",
    mission: "确保隐藏维度能平均分给每个 head。",
    intuition: "如果 10 块糖分给 3 个小队，就无法得到相同的 head_dim。",
    shape: "D % H == 0",
    before: "def __init__(self, d_model, num_heads):\n",
    after: "\n        raise ValueError(\"d_model must be divisible by num_heads\")\n    self.head_dim = d_model // num_heads",
    options: ["    if d_model % num_heads != 0:", "    if d_model == num_heads:", "    if num_heads % 2 == 0:"],
    answer: 0,
    hint: "% 会告诉你有没有余数。",
    success: "每个 head 都分到了同样多的维度。",
    sourcePages: "手册第 8 页",
  },
  {
    id: "split-reshape",
    title: "拆出小队",
    mission: "先把 D 拆成 H 和 Dₕ。",
    intuition: "reshape 只重新摆放分组，不改变元素总数。",
    shape: "[B,L,D] → [B,L,H,Dₕ]",
    before: "batch_size, seq_len, _ = x.shape\n",
    after: "\nreturn x.transpose(0, 2, 1, 3)",
    options: [
      "x = x.reshape(batch_size, seq_len, self.num_heads, self.head_dim)",
      "x = x.reshape(seq_len, batch_size, self.d_model)",
      "x = x.flatten()"
    ],
    answer: 0,
    hint: "目标尾部两维相乘必须仍然等于 D。",
    success: "隐藏维度成功拆成 H×Dₕ。",
    sourcePages: "手册第 9 页",
  },
  {
    id: "split-transpose",
    title: "把 head 搬到前面",
    mission: "让每个 head 拥有自己的 [L,Dₕ] 小桌面。",
    intuition: "Attention 希望批量同时计算 B 个样本、H 个 head。",
    shape: "[B,L,H,Dₕ] → [B,H,L,Dₕ]",
    before: "x = x.reshape(B, L, H, Dh)\n",
    after: "",
    options: ["return x.transpose(0, 2, 1, 3)", "return x.transpose(3, 2, 1, 0)", "return x.transpose(0, 1, 2, 3)"],
    answer: 0,
    hint: "只要交换 L 与 H，B 和 Dₕ 不动。",
    success: "每个 head 已坐到自己的工作台。",
    sourcePages: "手册第 9 页",
  },
  {
    id: "project-qkv",
    title: "制造 Q、K、V",
    mission: "用三组可学习矩阵投影同一个输入。",
    intuition: "同一个 token 要分别学会提问、被匹配和携带内容。",
    shape: "3 × [B,L,D]",
    before: "# Linear projections\n",
    after: "\nk = x @ self.W_k\nv = x @ self.W_v",
    options: ["q = x @ self.W_q", "q = self.W_q @ self.W_k", "q = np.sum(x)"],
    answer: 0,
    hint: "输入在左，投影矩阵在右。",
    success: "Query 已经拿到自己的放大镜。",
    sourcePages: "手册第 9-10 页",
  },
  {
    id: "score-matmul",
    title: "让 Q 找到 K",
    mission: "计算每个 Query 对每个 Key 的点积分数。",
    intuition: "K 的最后两维要从 [L,Dₕ] 翻成 [Dₕ,L]。",
    shape: "[B,H,L,Dₕ] × [B,H,Dₕ,L] → [B,H,L,L]",
    before: "# q, k: [B, H, L, Dh]\n",
    after: "",
    options: [
      "scores = q @ k.transpose(0, 1, 3, 2)",
      "scores = q @ k.transpose(3, 2, 1, 0)",
      "scores = q + k"
    ],
    answer: 0,
    hint: "只交换 K 的 L 与 Dₕ 两个轴。",
    success: "得到 [B,H,L,L] 注意力地图。",
    sourcePages: "手册第 10 页",
  },
  {
    id: "score-scale",
    title: "把分数缩小一点",
    mission: "除以 √Dₕ，让 Softmax 不会变得太“凶”。",
    intuition: "点积分数的标准差约随 √Dₕ 增长；除掉它能稳定 logits。",
    shape: "[B,H,L,L] → [B,H,L,L]",
    before: "scores = q @ k.transpose(0, 1, 3, 2)\n",
    after: "",
    options: [
      "scores = scores / np.sqrt(self.head_dim)",
      "scores = np.matmul(k, q) / np.sqrt(self.head_dim)",
      "scores = scores - np.max(scores, axis=-1, keepdims=True)"
    ],
    answer: 0,
    hint: "缩放只改变数值，不改变形状。",
    success: "[B,H,L,L] 形状正确，logits 温度也刚好。",
    sourcePages: "手册第 10 页",
  },
  {
    id: "causal-mask-code",
    title: "封住未来",
    mission: "生成严格上三角的布尔 Mask。",
    intuition: "k=1 表示主对角线保留，从它上方才开始遮住。",
    shape: "[L,L] bool",
    before: "if causal:\n",
    after: "\n    scores = np.where(future_mask[None, None, :, :], -1e30, scores)",
    options: [
      "    future_mask = np.triu(np.ones((seq_len, seq_len), dtype=bool), k=1)",
      "    future_mask = np.eye(seq_len, dtype=bool)",
      "    future_mask = np.tril(np.ones((seq_len, seq_len)), k=-1)"
    ],
    answer: 0,
    hint: "未来在矩阵的右上角。",
    success: "未来偷看怪被挡在 Softmax 门外。",
    sourcePages: "手册第 10 页",
  },
  {
    id: "padding-mask-code",
    title: "忽略空座位",
    mission: "把 [B,L] 的有效 token 标记广播到所有 head 和 Query。",
    intuition: "Key 位置是最后一维；中间插入两个长度 1 的轴就能广播。",
    shape: "[B,L] → [B,1,1,L]",
    before: "if padding_mask is not None:\n",
    after: "\n    scores = np.where(valid_keys, scores, -1e30)",
    options: [
      "    valid_keys = padding_mask[:, None, None, :].astype(bool)",
      "    valid_keys = padding_mask[None, :, :, None]",
      "    valid_keys = padding_mask.reshape(-1)"
    ],
    answer: 0,
    hint: "B 要留在第 0 轴，Key 的 L 要留在最后一轴。",
    success: "所有空座位都不会被注意到。",
    sourcePages: "手册第 10 页",
  },
  {
    id: "normalize-code",
    title: "分数变概率",
    mission: "在 Key 这一维做 Softmax。",
    intuition: "每个 Query 对所有 Keys 的注意力权重应该加起来等于 1。",
    shape: "sum(axis=-1) = 1",
    before: "# scores: [B, H, L, L]\n",
    after: "",
    options: ["attention_weights = softmax(scores, axis=-1)", "attention_weights = softmax(scores, axis=0)", "attention_weights = np.mean(scores)"],
    answer: 0,
    hint: "Key 位置是最后一个 L。",
    success: "每个 Query 都分配好了 100% 注意力。",
    sourcePages: "手册第 10 页",
  },
  {
    id: "aggregate-values",
    title: "读取真正内容",
    mission: "用注意力概率加权 Value。",
    intuition: "Q/K 只负责决定去哪找，V 才是带回来的内容。",
    shape: "[B,H,L,L] × [B,H,L,Dₕ] → [B,H,L,Dₕ]",
    before: "attention_weights = softmax(scores, axis=-1)\n",
    after: "",
    options: ["context = attention_weights @ v", "context = attention_weights @ k", "context = q @ v.transpose(0, 1, 3, 2)"],
    answer: 0,
    hint: "最后一步读取的是 Value。",
    success: "每个 head 都带回了加权信息。",
    sourcePages: "手册第 10 页",
  },
  {
    id: "merge-output",
    title: "合并调查报告",
    mission: "合并所有 head，再经过输出投影 Wₒ。",
    intuition: "先把 H 和 Dₕ 拼回 D，最后让不同 head 的信息再次混合。",
    shape: "[B,H,L,Dₕ] → [B,L,D]",
    before: "context = self._merge_heads(context)\n",
    after: "\nreturn output, attention_weights",
    options: ["output = context @ self.W_o", "output = context @ self.W_q", "output = np.sum(context)"],
    answer: 0,
    hint: "它叫 output projection，所以使用 W_o。",
    success: "完整 MHA 机器点亮！你没有死背代码，而是走通了每个逻辑块。",
    sourcePages: "手册第 10-11 页",
  },
];

export const totalQuestCount = worlds.reduce((sum, world) => sum + world.quests.length, 0);

export const interviewChecklist = [
  "从张量形状推导 MHA 的完整复杂度",
  "解释缩放因子为什么是 √dₖ",
  "不看资料拼出 Causal MHA",
  "准确区分 MQA、GQA、MLA",
  "从参数计算 KV Cache GiB",
  "说明 FlashAttention 优化 IO 而非渐近 FLOPs",
  "推导 RoPE 点积为何依赖 n−m",
  "比较 PI、NTK、YaRN、LongRoPE",
  "解释 SFT 的边界与 PPO 四角色",
  "解释 Value、Q、Advantage、GAE、Clip 与 KL",
  "从 KL 正则化目标理解 DPO",
  "写出 GRPO 组相对 Advantage 与同分失效",
  "解释 DAPO 四项改进",
  "为代码、Web 或工具 Agent 设计可验证 Reward",
];
